import * as client from "_graphql-types";
import * as types from "./types";
import { EnsureAllKeys } from "../helpers/types";
type Sdk = ReturnType<typeof client.getSdk>;

/*********************************************************************/
// Data Provider Functions
/*********************************************************************/

/*********************************************************************/
async function getList(
  sdk: Sdk,
  params: types.GetListParams<client.InvestmentPrivateQuartileGroupCategorySortEnum>
) {
  const {
    pagination: { page, perPage },
    sort: { field, order },
    filter,
  } = params;

  const {
    investmentPrivateQuartileGroupCategoryList: { items: data, total },
  } = await sdk.getListInvestmentPrivateQuartileGroupCategory({
    page: {
      offset: (page - 1) * perPage,
      limit: perPage,
    },
    sort: [{ field, order }],
    filter: {
      isDerived: false,
      ...filter,
    },
  });

  return {
    data,
    total,
  };
}

/*********************************************************************/
async function getOne(sdk: Sdk, params: types.GetOneParams) {
  const id = Number(params.id);
  const { investmentPrivateQuartileGroupCategory: data } =
    await sdk.getOneInvestmentPrivateQuartileGroupCategory({
      id: Number(id),
    });
  return {
    data,
  };
}

/*********************************************************************/
async function getMany(sdk: Sdk, params: types.GetManyParams) {
  const ids = params.ids.map(Number);

  const { investmentPrivateQuartileGroupCategoryMany: data } =
    await sdk.getManyInvestmentPrivateQuartileGroupCategory({
      ids: ids.map(Number),
    });

  return {
    data,
  };
}

/*********************************************************************/
async function getManyReference(
  sdk: Sdk,
  params: types.GetManyReferenceParams<client.InvestmentPrivateQuartileGroupCategorySortEnum>
) {
  const id = Number(params.id);
  const {
    pagination: { page, perPage },
    sort: { field, order },
    filter,
    target,
  } = params;

  const {
    investmentPrivateQuartileGroupCategoryList: { items: data, total },
  } = await sdk.getListInvestmentPrivateQuartileGroupCategory({
    page: {
      offset: (page - 1) * perPage,
      limit: perPage,
    },
    sort: [{ field, order }],
    filter: { isDerived: false, ...filter, [target]: Number(id) },
  });

  return {
    data,
    total,
  };
}

/*********************************************************************/
async function create(
  sdk: Sdk,
  params: types.CreateParams<
    NonNullable<
      client.GetOneInvestmentPrivateQuartileGroupCategoryQuery["investmentPrivateQuartileGroupCategory"]
    >
  >
) {
  const { data } = params;
  const input: EnsureAllKeys<
    client.CreateInvestmentPrivateQuartileGroupCategoryMutationVariables["input"]
  > = {
    investmentId: data.investmentId,
    privateQuartileGroupCategoryId: data.privateQuartileGroupCategoryId,
    isDefault: data.isDefault,
  };

  const {
    addInvestmentPrivateQuartileGroupCategory: { id },
  } = await sdk.createInvestmentPrivateQuartileGroupCategory({
    input,
  });
  return await getOne(sdk, { id: id! });
}

/*********************************************************************/
async function update(
  sdk: Sdk,
  params: types.UpdateParams<
    NonNullable<
      client.GetOneInvestmentPrivateQuartileGroupCategoryQuery["investmentPrivateQuartileGroupCategory"]
    >
  >
) {
  const id = Number(params.id);
  const { data } = params;

  const input: EnsureAllKeys<
    client.UpdateInvestmentPrivateQuartileGroupCategoryMutationVariables["input"]
  > = {
    investmentId: data.investmentId,
    privateQuartileGroupCategoryId: data.privateQuartileGroupCategoryId,
    isDefault: data.isDefault,
  };

  await sdk.updateInvestmentPrivateQuartileGroupCategory({
    id,
    input,
  });
  return await getOne(sdk, { id });
}

async function deleteOne(sdk: Sdk, params: types.DeleteParams) {
  const id = Number(params.id);
  await sdk.deleteInvestmentPrivateQuartileGroupCategory({
    id,
  });
  return { data: { id } };
}

/*********************************************************************/
export const investmentPrivateQuartileGroupCategory = types.dataProvider({
  getList,
  getOne,
  getMany,
  getManyReference,
  create,
  update,
  delete: deleteOne,
});
